import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  HubSection,
  EmptyState,
  ErrorState,
  LoadingState,
  RecencyBadge,
  AskAboutThis,
} from "@/components/research-hub";

// Reader-clean meeting notes. Reads the same table the ops capsule pages use
// (research_meeting_capsules) but shows only the finished, human-readable note:
// a friendly title, when the meeting happened, and the consolidated summary.
// All merge/admin/ops affordances, raw ids, status enums and JSON are hidden.

type Row = Record<string, any>;
// Read permissively — RLS scopes the rows. Casting keeps us compiling even if the
// generated types don't surface this table.
const sb = supabase as any;

function useMeetingNotes() {
  return useQuery({
    queryKey: ["research_hub_meeting_notes"],
    staleTime: 60 * 1000,
    queryFn: async () => {
      // Newest meeting first; carry only the fields the reader needs.
      const { data, error } = await sb
        .from("research_meeting_capsules")
        .select("id, title, organisation, meeting_at, status, consolidated_summary, synthesised_at")
        .order("meeting_at", { ascending: false, nullsFirst: false })
        .limit(200);
      if (error) throw error;
      const rows = ((data as Row[]) ?? []).filter(Boolean);
      // Only show notes that are FINISHED and have something to read. The reader must never see the
      // working states — proposed (unconfirmed), failed, rejected, or anything mid-synthesis. Show a
      // note only when it carries a body AND it has reached a finished status (synthesised/confirmed),
      // or has actually been synthesised (synthesised_at set) and isn't in a known working state.
      const FINISHED = new Set(["synthesised", "synthesized", "confirmed"]);
      const WORKING = new Set([
        "proposed",
        "pending",
        "synthesising",
        "synthesizing",
        "processing",
        "queued",
        "rejected",
        "failed",
        "error",
      ]);
      return rows.filter((r) => {
        const status = (r.status ?? "").toString().toLowerCase();
        const hasBody = (r.consolidated_summary ?? "").toString().trim().length > 0;
        if (!hasBody || WORKING.has(status)) return false;
        return FINISHED.has(status) || !!r.synthesised_at;
      });
    },
  });
}

// Pull a short, plain lead paragraph from a markdown summary for the collapsed view.
function leadParagraph(markdown: string | null | undefined): string {
  const text = (markdown ?? "").toString();
  if (!text.trim()) return "";
  // Strip headings/bullets/emphasis down to a clean sentence or two.
  const cleaned = text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^[-*+]\s+/gm, "")
    .replace(/[*_>`#]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (cleaned.length <= 220) return cleaned;
  return cleaned.slice(0, 220).replace(/\s+\S*$/, "") + "…";
}

export default function MeetingNotes() {
  const notes = useMeetingNotes();

  return (
    <div className="space-y-8 max-w-3xl">
      <HubSection
        title="Meeting notes"
        description="Write-ups from recent meetings, ready to read."
      >
        {notes.isLoading ? (
          <LoadingState rows={3} />
        ) : notes.error ? (
          <ErrorState onRetry={() => notes.refetch()} />
        ) : (notes.data ?? []).length === 0 ? (
          <EmptyState
            title="No meeting notes yet."
            body="When a meeting has been written up, it will appear here."
          />
        ) : (
          <div className="space-y-3">
            {notes.data!.map((n, i) => (
              <MeetingNoteCard key={n.id ?? i} note={n} />
            ))}
          </div>
        )}
      </HubSection>
    </div>
  );
}

function MeetingNoteCard({ note }: { note: Row }) {
  const title = (note.title ?? "").toString().trim() || "Meeting note";
  const org = (note.organisation ?? "").toString().trim();
  const date = note.meeting_at ?? note.synthesised_at;
  const summary = (note.consolidated_summary ?? "").toString();
  const lead = leadParagraph(summary);

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading text-base font-medium truncate">{title}</div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {org && <span className="font-body text-xs text-muted-foreground">{org}</span>}
            {org && date && <span className="font-body text-xs text-muted-foreground">·</span>}
            {date && <RecencyBadge date={date} />}
          </div>
        </div>
        <AskAboutThis prefill={`Tell me about the meeting note: ${title}.`} label="" />
      </div>

      {lead && <p className="font-body text-sm text-muted-foreground">{lead}</p>}

      <Accordion type="single" collapsible className="border-t pt-1">
        <AccordionItem value="body" className="border-0">
          <AccordionTrigger className="font-body text-sm text-primary hover:no-underline py-2">
            Read the full note
          </AccordionTrigger>
          <AccordionContent>
            <div className="prose prose-sm max-w-none font-body prose-headings:font-heading prose-a:text-primary">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary}</ReactMarkdown>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}
